import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { useNavigationStack } from '../../../context/NavigationStackContext';
import { useFinishingTrainingExercises } from '../../../hooks/useFinishingTrainingExercises';
import { useGetExerciseOptions } from '../../../redux/api/exerciseConfigAPI';
import {
  useBulkUpdateTrainingSets,
  useDeleteTraining,
  useGetTrainingById,
  usePatchTrainingSetsOrder,
} from '../../../redux/api/trainingsAPI';
import { ITraining } from '../../../types/interfaces/training';
import { ITrainingSet } from '../../../types/interfaces/training-set';
import { groupBy } from '../../../utils/collections/array-utils';
import { orderTraining } from '../../../utils/training/set-utils';
import { useEditTraining } from '../edit-training/useEditTraining';

export function useFinishedTrainingPage(trainingId: string) {
  const { back } = useNavigationStack();
  const {
    data: training,
    isLoading,
    isFetching,
    isError,
    error,
    refetch,
  } = useGetTrainingById(trainingId);
  const [showDeleteConfirmation, setShowDeleteConfirmation] =
    useState<boolean>(false);
  const [deleteTrainingApi, { isLoading: isDeletingTraining }] =
    useDeleteTraining();
  const [bulkUpdateTrainingSets, { isLoading: isUpdatingTrainingSets }] =
    useBulkUpdateTrainingSets();
  const [patchTrainingSetsOrder, { isLoading: isSavingSetsOrder }] =
    usePatchTrainingSetsOrder();
  const { exercises, isLoading: isTrainingExercisesLoading } =
    useFinishingTrainingExercises(training?.id);
  const { data: exerciseOptions } = useGetExerciseOptions();
  const [searchParams] = useSearchParams();
  const editParam = searchParams.get('edit');
  const [trainingEditing, setTrainingEditing] = useState(
    () => editParam === 'true',
  );
  const [editedTrainingSetGroup, setEditedTrainingSetGroup] = useState<
    ITrainingSet[] | null
  >(null);
  const [sortSetsModalOpen, setSortSetsModalOpen] = useState(false);
  const { getEmptyTrainingSet } = useEditTraining(null, training?.id);

  useEffect(() => {
    if (training?.permissions && !training.permissions.canEditInfo) {
      setTrainingEditing(false);
    }
  }, [training]);

  const orderedTraining: ITraining = useMemo(() => {
    if (
      !training ||
      !exercises ||
      !exerciseOptions ||
      isTrainingExercisesLoading
    ) {
      return null;
    }

    return orderTraining(training, exercises, exerciseOptions);
  }, [training, exercises, exerciseOptions, isTrainingExercisesLoading]);

  const trainingSetGroups = useMemo(
    () =>
      orderedTraining?.trainingSets
        ? groupBy(orderedTraining?.trainingSets, 'setNumber')
        : null,
    [orderedTraining],
  );

  function deleteTraining() {
    deleteTrainingApi({ id: trainingId })
      .unwrap()
      .then(() => {
        setShowDeleteConfirmation(false);
        back();
      })
      .catch(console.error);
  }

  function onEditTrainingSetSubmit(trainingSets: ITrainingSet[]) {
    if (!training?.id) return;
    bulkUpdateTrainingSets({
      trainingId: training.id,
      sets: trainingSets,
    })
      .unwrap()
      .then(() => {
        setEditedTrainingSetGroup(null);
      })
      .catch(console.error);
  }

  function onEditTrainingSetClick(trainingSetGroup: ITrainingSet[]) {
    setEditedTrainingSetGroup(trainingSetGroup);
  }

  function onNewTrainingSetClick() {
    if (!training?.id) return;
    setEditedTrainingSetGroup([
      getEmptyTrainingSet(training.trainingSets, training.id),
    ]);
  }

  async function onSaveSetsOrder(
    sets: { id: string; setNumber: number; orderNumber: number }[],
  ) {
    if (!training?.id) return;
    await patchTrainingSetsOrder({
      trainingId: training.id,
      sets,
    }).unwrap();
    setSortSetsModalOpen(false);
  }

  return {
    isLoading,
    isFetching,
    isError,
    error,
    refetch,
    training,
    exercises,
    trainingSetGroups,
    exerciseOptions,
    trainingEditing,
    setTrainingEditing,
    editedTrainingSetGroup,
    setEditedTrainingSetGroup,
    onEditTrainingSetSubmit,
    onEditTrainingSetClick,
    onNewTrainingSetClick,
    sortSetsModalOpen,
    setSortSetsModalOpen,
    onSaveSetsOrder,
    isSavingTrainingSets: isSavingSetsOrder || isUpdatingTrainingSets,
    isSavingSetsOrder,
    orderedTrainingSets: orderedTraining?.trainingSets ?? [],
    showDeleteConfirmation,
    setShowDeleteConfirmation,
    deleteTraining,
    isDeletingTraining,
  };
}
